import axios from "axios";

// Mirrors CpSourcingApis.js's BASE pattern — same env var, same backend.
// Sales-mode-only build: CRM mode, Transfer by Project, and CP-to-CP
// Transfer API calls live in the full (non-sales-only) branch instead.
// Update CP Sourcing Manager and Rearrange a Team are built here.
const BASE = `${process.env.REACT_APP_PAYMENT_LINK_URL}api/work-transfer`;

// "Who's work is moving" picker: Sales/CRM users who currently own at least
// one lead — both active and disabled (a disabled user's leads are exactly
// what this screen exists to hand off).
export const getEligibleSourceUsers = async () => {
  const res = await axios.get(`${BASE}/move-work/eligible-source-users`);
  return res.data; // {success, data:{users}}
};

// "Assign to" / recipient picker: active Sales/CRM users — no "already owns
// work" requirement.
export const getEligibleRecipientUsers = async () => {
  const res = await axios.get(`${BASE}/move-work/eligible-recipient-users`);
  return res.data; // {success, data:{users}}
};

// "Who's work is moving" overview tiles (2026-08-19) — the one count that
// isn't derivable from the already-loaded eligible-source-users list (users
// who own zero leads but still have a pending approval sitting with them).
export const getMoveWorkSourceOverview = async () => {
  const res = await axios.get(`${BASE}/move-work/source-overview`);
  return res.data; // {success, data:{zeroLeadsPendingApprovalsCount}}
};

// Phase 2 — "Move someone's work": bucket preview for a user. "Self + team"
// scope was removed entirely (2026-08-26) — always the selected user's own
// leads now, never a manager's direct reports too.
export const getMoveWorkBuckets = async ({ userId, dateFrom, dateTo, includeCrmTagged, includeAgreementDone }) => {
  const params = { userId };
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  // Allotment bucket's own opt-in toggles (2026-08-25) — omitted entirely
  // rather than sent as "false" so the backend's own defaults (both
  // excluded) are the single source of truth, not duplicated here.
  if (includeCrmTagged) params.includeCrmTagged = "true";
  if (includeAgreementDone) params.includeAgreementDone = "true";
  const res = await axios.get(`${BASE}/move-work/buckets`, { params });
  return res.data; // {success, data:{buckets, totalEligible, unbucketedStatusesSeen}}
};

// Move-someone's-work (Sales mode) pending-approval check — run before Review
// opens. Not scoped by bucket/leads — returns every approval currently
// sitting with this person, bifurcated into its three real kinds:
// Cancellation and Unit Shift are pending with a specific stored user id,
// regardless of lead ownership; Site Visit has no such column — its
// "pending with X" is computed live from Leads.userParentId instead.
export const getMoveWorkPendingApprovals = async ({ userId }) => {
  const res = await axios.get(`${BASE}/move-work/pending-approvals`, { params: { userId } });
  return res.data; // {success, data:{cancelRequests, changeUnitRequests, siteVisitApprovals}}
};

// Intent-based confirm call — the frontend sends "what to do" (buckets,
// recipients), the backend builds the raw operations internally. Preferred
// over the generic /execute for the built-in entry points.
export const executeMoveWorkFlow = async ({
  userId,
  dateFrom,
  dateTo,
  bucketNames,
  excludedLeadIds,
  recipients,
  distribution,
  reason,
  performedBy,
  cancelRequestIds,
  changeUnitRequestIds,
  siteVisitApprovalIds,
}) => {
  const res = await axios.post(`${BASE}/move-work/execute`, {
    userId,
    dateFrom,
    dateTo,
    bucketNames,
    excludedLeadIds,
    recipients,
    distribution,
    reason,
    performedBy,
    cancelRequestIds,
    changeUnitRequestIds,
    siteVisitApprovalIds,
  });
  return res.data; // {success, data:{transferBatchId, operationsApplied}} or {success:false, error, message, details}
};

// FR-12 — block sequencing. Called by the EXISTING Block button in
// UsersPage.jsx before it deactivates a user.
export const getBlockCheck = async (userId) => {
  const res = await axios.get(`${BASE}/block-check/${userId}`);
  return res.data; // {success, data:{hasActiveWork, summary:{leadsCount, leadBuckets, directReportsCount}}}
};

// "Rearrange a team" (2026-08-26) — "current manager" picker: only people
// who actually have at least one direct report.
export const getEligibleManagers = async () => {
  const res = await axios.get(`${BASE}/team/eligible-managers`);
  return res.data; // {success, data:{users}}
};

// Checklist of a manager's direct reports, for the tick-subset-then-assign UI.
export const getDirectReports = async (managerId) => {
  const res = await axios.get(`${BASE}/team/direct-reports`, { params: { managerId } });
  return res.data; // {success, data:{reports}}
};

// "New manager" picker, restricted by role hierarchy (Roles.parentRoleId) —
// e.g. a Sales Manager's only valid new manager is whoever holds the Project
// Head role. requiredRoleId/requiredRoleTitle come back null when roleId has
// no parentRoleId (top of the hierarchy) — show that as "no valid role above
// this one" rather than an empty picker.
export const getEligibleNewManagersForRole = async (roleId) => {
  const res = await axios.get(`${BASE}/team/eligible-new-managers`, { params: { roleId } });
  return res.data; // {success, data:{requiredRoleId, requiredRoleTitle, users}}
};

// Pending-approval check, run before Review — Cancellation/Unit Shift/Site
// Visit approvals currently pending with the OLD manager, for leads owned by
// the reports about to move. Same response shape as getMoveWorkPendingApprovals.
export const getTeamPendingApprovals = async ({ managerId, reportUserIds }) => {
  const res = await axios.get(`${BASE}/team/pending-approvals`, {
    params: { managerId, reportUserIds: (reportUserIds || []).join(",") },
  });
  return res.data; // {success, data:{cancelRequests, changeUnitRequests, siteVisitApprovals}}
};

export const executeRearrangeTeamFlow = async ({
  managerId,
  reportUserIds,
  newManagerId,
  cancelRequestIds,
  changeUnitRequestIds,
  siteVisitApprovalIds,
  reason,
  performedBy,
}) => {
  const res = await axios.post(`${BASE}/team/rearrange`, {
    managerId,
    reportUserIds,
    newManagerId,
    cancelRequestIds,
    changeUnitRequestIds,
    siteVisitApprovalIds,
    reason,
    performedBy,
  });
  return res.data; // {success, data:{transferBatchId, operationsApplied}} or {success:false, error, message, details}
};

// "Update CP Sourcing Manager" — source/recipient pickers are the CP
// Sourcing Manager persona (Users.isCpSourcingUser), NOT Sales/CRM, so these
// deliberately don't reuse getEligibleSourceUsers/getEligibleRecipientUsers
// above. Source is not active-only (a departed/blocked manager's CPs are
// exactly what this hands off); recipient is active-only.
export const getCpSourcingSourceUsers = async () => {
  const res = await axios.get(`${BASE}/cp-sourcing-manager/source-users`);
  return res.data; // {success, data:{users}}
};

export const getCpSourcingRecipientUsers = async () => {
  const res = await axios.get(`${BASE}/cp-sourcing-manager/recipient-users`);
  return res.data; // {success, data:{users}}
};

// Task #1's picker: approved CPs currently managed by userId, filterable by
// cpType / created_at range / free-text search on billingName or cpDisplayId.
export const getEligibleCpsForSourcingManager = async ({ userId, cpType, dateFrom, dateTo, search }) => {
  const params = { userId };
  if (cpType?.length) params.cpType = cpType.join(",");
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  if (search) params.search = search;
  const res = await axios.get(`${BASE}/cp-sourcing-manager/eligible-cps`, { params });
  return res.data; // {success, data:{cps}}
};

export const executeCpSourcingManagerChangeFlow = async ({ fromUserId, toUserId, cpIds, reason, performedBy }) => {
  const res = await axios.post(`${BASE}/cp-sourcing-manager/execute`, { fromUserId, toUserId, cpIds, reason, performedBy });
  return res.data; // {success, data:{transferBatchId, operationsApplied}} or {success:false, error, message, details}
};
